import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { MutationError } from '@jira/platform__utils-mutation-error/src/index.tsx';
import type { MenuItemReorderOperation } from '../../utils/constants.tsx';
import { ANALYTIC_PAYLOAD } from '../../common/constants.tsx';
import { menuItemReorderOperationToAction } from '../../utils/operation-action.tsx';
import type { useUpdateFavouriteOrderMutation } from './__generated__/useUpdateFavouriteOrderMutation.graphql';
import { useHandleError } from './error-handling.tsx';
import { optimisticUpdater } from './optimistic-updater/optimistic-updater-without-query.tsx';

type ProjectId = string;

export const useUpdateFavouriteOrder = (): ((
	operation: MenuItemReorderOperation,
	projectId: ProjectId,
	relativeProjectId?: ProjectId,
) => void) => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [commitMutation] = useMutation<useUpdateFavouriteOrderMutation>(graphql`
		mutation useUpdateFavouriteOrderMutation($input: JiraReorderSidebarMenuItemInput!) {
			jira_reorderProjectsSidebarMenuItem(input: $input) @optIn(to: "JiraSidebarMenu") {
				id
				favourites(first: 15) {
					__id
					pageInfo {
						hasNextPage
					}
					edges {
						node {
							id
							projectKey: key
							...Nav4ProjectMenuItem
						}
					}
				}
			}
		}
	`);

	const handleError = useHandleError();

	const updateFavouriteOrder = useCallback(
		(operation: MenuItemReorderOperation, projectId: ProjectId, relativeProjectId?: ProjectId) => {
			commitMutation({
				variables: {
					input: {
						cloudId,
						reorderMode: operation,
						menuItem: {
							// eslint-disable-next-line jira/relay/mutation-variables-from-relay-data
							itemId: projectId,
						},
						// include relative menu item if provided

						...(relativeProjectId && {
							relativeMenuItem: {
								itemId: relativeProjectId,
							},
						}),
					},
				},
				optimisticUpdater: (store) => {
					optimisticUpdater(store, operation, projectId, cloudId, relativeProjectId);
				},
				onCompleted: (_, errors) => {
					if (errors && errors.length > 0) {
						const error = new MutationError(errors[0]);
						handleError(error);
					} else {
						const analyticsEvent = createAnalyticsEvent({});
						fireTrackAnalytics(analyticsEvent, {
							...ANALYTIC_PAYLOAD.UPDATED_ORDER,
							attributes: {
								...ANALYTIC_PAYLOAD.ATTRIBUTES,
								itemId: projectId,
								action: menuItemReorderOperationToAction(operation),
							},
						});
					}
				},
				onError: (error) => {
					handleError(new MutationError(error));
				},
			});
		},
		[commitMutation, cloudId, handleError, createAnalyticsEvent],
	);

	return updateFavouriteOrder;
};
